/* ============================================
   Artemis Theme — Download Proof
   --------------------------------------------
   Generates a PDF engraving "proof" for personalizable PDPs (trophy / resin),
   100% client-side and dependency-free.

   One shared engine, two callers:
     • The PDP proof_download block button → proofs the single item the customer
       configured (the personalization line inputs).
     • The bulk tool → proofs EVERY row at once (one box per item), so the whole
       order can be checked before adding to cart.

   Each item is a "box" sized to the SELECTED variant's plate
   (window.PROOF_DIMENSIONS[variantId] = {width, height, unit, image}). Boxes are
   tiled into a grid on a Letter sheet and paginated (mirroring the reference
   PyMuPDF routine: columns_per_page, row wrap, new page on overflow). Each
   sheet carries a header band — the variant image upper-left + the product
   title. Inside every box the lines are auto-sized to the largest font that
   fits, distributed with equal gaps, and centered; a red outline marks the
   engraving area.

   Pages are rasterized to canvas, exported as JPEG, and stitched into a minimal
   hand-built multi-page PDF whose pages are real-size Letter (72pt = 1in).

   Exposes window.ArtemisProof.generate(opts).
   ============================================ */

(function () {
  'use strict';

  var DPI = 150;             // raster resolution (px per inch)
  var PT_PER_IN = 72;        // PDF points per inch
  var PX_PER_PT = DPI / PT_PER_IN;

  // Sheet + box geometry — mirror the reference proofing routine.
  var PAGE_W_IN = 8.5;       // Letter
  var PAGE_H_IN = 11;
  var MARGIN_IN = 0.1;       // gap around boxes (and the page edge)
  var HEADER_IN = 1.0;       // header band (variant image + title) at the top
  var MAX_FONT_PT = 30;      // largest engraving size to try
  var MIN_FONT_PT = 5;       // smallest before we clamp and draw anyway
  var LINE_HEIGHT = 1.2;     // line height as a multiple of font size
  var PROOF_PAD_IN = 0.04;   // tiny inset so text never touches the red outline

  /* --- small utilities --- */

  // Normalize smart quotes to straight ones (the reference clean_text), so the
  // engraving reads exactly as it will be cut.
  function cleanText(s) {
    return String(s == null ? '' : s)
      .replace(/[‘’‚‛]/g, "'")
      .replace(/[“”„‟]/g, '"');
  }

  // Plate size → inches (the PDF page is sized in points = inches * 72).
  function toInches(value, unit) {
    switch (String(unit || 'in').toLowerCase()) {
      case 'mm': return value / 25.4;
      case 'cm': return value / 2.54;
      default:   return value;   // in / inch / inches / "
    }
  }

  function loadImage(url) {
    return new Promise(function (resolve) {
      if (!url) { resolve(null); return; }
      var img = new Image();
      img.crossOrigin = 'anonymous';   // needed so the canvas isn't tainted
      img.onload = function () { resolve(img); };
      img.onerror = function () { resolve(null); };
      img.src = url;
    });
  }

  function dataURLToBytes(dataURL) {
    var bin = atob(dataURL.split(',')[1]);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }

  /* --- drawing --- */

  // One engraving box: fit the lines, distribute them with equal gaps, center
  // each, then outline the plate in red. Geometry in pixels.
  function drawBox(ctx, box, lines) {
    var n = lines.length;
    if (n) {
      var font = 'Arial, Helvetica, sans-serif';
      // Keep the text off the red outline: fit and place it inside a slightly
      // inset area so nothing ever touches the edge lines.
      var pad = Math.max(2, Math.round(PROOF_PAD_IN * DPI));
      var innerX = box.x + pad;
      var innerY = box.y + pad;
      var innerW = box.w - pad * 2;
      var innerH = box.h - pad * 2;

      // Largest integer-point size where all lines fit the height AND the
      // widest line fits the width (step MAX→MIN, like the reference).
      var fontPt = MAX_FONT_PT;
      for (; fontPt >= MIN_FONT_PT; fontPt--) {
        var fpx = fontPt * PX_PER_PT;
        if (n * fpx * LINE_HEIGHT <= innerH) {
          ctx.font = fpx + 'px ' + font;
          var maxW = 0;
          lines.forEach(function (ln) { maxW = Math.max(maxW, ctx.measureText(ln).width); });
          if (maxW <= innerW) break;
        }
      }
      if (fontPt < MIN_FONT_PT) fontPt = MIN_FONT_PT;   // clamp + draw anyway

      var fontPx = fontPt * PX_PER_PT;
      var lineH = fontPx * LINE_HEIGHT;
      var gap = (innerH - n * lineH) / (n + 1);          // equal spacing

      ctx.font = fontPx + 'px ' + font;
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';

      var lineY = innerY + gap;
      lines.forEach(function (ln) {
        var lw = ctx.measureText(ln).width;
        var xLine = innerX + (innerW - lw) / 2;
        ctx.fillText(ln, xLine, lineY + fontPx);         // baseline = lineY + font
        lineY += lineH + gap;
      });
    }

    ctx.strokeStyle = '#ff0000';
    ctx.lineWidth = Math.max(1, 0.5 * PX_PER_PT);
    ctx.strokeRect(box.x, box.y, box.w, box.h);
  }

  // Header band: variant image upper-left, product title to its right, hairline
  // underneath. Returns the band's bottom (where the grid starts).
  function drawHeader(ctx, image, title, marginPx, pageW) {
    var headerH = HEADER_IN * DPI;
    var pad = marginPx;
    var imgW = 0;
    if (image && image.width && image.height) {
      var maxH = headerH - pad * 2;
      var maxW = pageW * 0.28;
      var r = Math.min(maxW / image.width, maxH / image.height);
      imgW = image.width * r;
      ctx.drawImage(image, marginPx, pad, imgW, image.height * r);
    }
    if (title) {
      ctx.fillStyle = '#1a1a1a';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.font = '700 ' + Math.round(0.2 * DPI) + 'px Arial, Helvetica, sans-serif';
      ctx.fillText(title, marginPx + (imgW ? imgW + marginPx : 0), pad + (headerH - pad * 2) / 2);
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = Math.max(1, 0.5 * PX_PER_PT);
    ctx.beginPath();
    ctx.moveTo(marginPx, headerH);
    ctx.lineTo(pageW - marginPx, headerH);
    ctx.stroke();
    return headerH;
  }

  // Tile every block (an array of lines) into boxes across one or more Letter
  // pages. Returns the page canvases + their point size.
  function renderSheets(blocks, dims, image, title) {
    var wIn = toInches(dims.width, dims.unit);
    var hIn = toInches(dims.height, dims.unit);
    var boxW = wIn * DPI;
    var boxH = hIn * DPI;
    var margin = MARGIN_IN * DPI;
    var pageW = Math.round(PAGE_W_IN * DPI);
    var pageH = Math.round(PAGE_H_IN * DPI);
    var hasHeader = !!(image || title);

    var cols = Math.max(1, Math.floor((pageW - margin) / (boxW + margin)));
    var xPositions = [];
    for (var c = 0; c < cols; c++) xPositions.push(margin + c * (boxW + margin));

    var pages = [];
    var ctx, xIndex, y;
    function newPage() {
      var canvas = document.createElement('canvas');
      canvas.width = pageW;
      canvas.height = pageH;
      ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageW, pageH);
      var top = hasHeader ? drawHeader(ctx, image, title, margin, pageW) : 0;
      xIndex = 0;
      y = margin + top;
      pages.push(canvas);
    }
    newPage();

    blocks.forEach(function (lines) {
      if (y + boxH + margin > pageH) newPage();
      if (xIndex >= cols) {
        xIndex = 0;
        y += boxH + margin;
        if (y + boxH + margin > pageH) newPage();
      }
      drawBox(ctx, { x: xPositions[xIndex], y: y, w: boxW, h: boxH }, lines);
      xIndex += 1;
    });

    return { pages: pages, wPt: PAGE_W_IN * PT_PER_IN, hPt: PAGE_H_IN * PT_PER_IN };
  }

  /* --- minimal multi-page PDF (each page = one full-page JPEG, no deps) --- */
  function buildPdf(pages) {
    var enc = new TextEncoder();
    var chunks = [];
    var offset = 0;
    var xref = [];
    function put(data) {
      var bytes = typeof data === 'string' ? enc.encode(data) : data;
      chunks.push(bytes);
      offset += bytes.length;
    }

    var N = pages.length;
    var kids = [];
    for (var i = 0; i < N; i++) kids.push((3 + i * 3) + ' 0 R');

    put('%PDF-1.3\n');
    xref[1] = offset; put('1 0 obj\n<</Type/Catalog/Pages 2 0 R>>\nendobj\n');
    xref[2] = offset; put('2 0 obj\n<</Type/Pages/Kids[' + kids.join(' ') + ']/Count ' + N + '>>\nendobj\n');

    for (i = 0; i < N; i++) {
      var pg = pages[i];
      var pageN = 3 + i * 3, contentN = 4 + i * 3, imgN = 5 + i * 3;

      xref[pageN] = offset;
      put(pageN + ' 0 obj\n<</Type/Page/Parent 2 0 R/MediaBox[0 0 ' + pg.wPt.toFixed(2) + ' ' +
        pg.hPt.toFixed(2) + ']/Resources<</XObject<</Im0 ' + imgN + ' 0 R>>>>/Contents ' +
        contentN + ' 0 R>>\nendobj\n');

      var content = 'q ' + pg.wPt.toFixed(2) + ' 0 0 ' + pg.hPt.toFixed(2) + ' 0 0 cm /Im0 Do Q';
      xref[contentN] = offset;
      put(contentN + ' 0 obj\n<</Length ' + content.length + '>>\nstream\n' + content + '\nendstream\nendobj\n');

      xref[imgN] = offset;
      put(imgN + ' 0 obj\n<</Type/XObject/Subtype/Image/Width ' + pg.pxW + '/Height ' + pg.pxH +
        '/ColorSpace/DeviceRGB/BitsPerComponent 8/Filter/DCTDecode/Length ' + pg.jpeg.length + '>>\nstream\n');
      put(pg.jpeg);
      put('\nendstream\nendobj\n');
    }

    var count = 3 + N * 3;   // objects 0 .. (2 + N*3)
    var xrefOffset = offset;
    var rows = ['xref', '0 ' + count, '0000000000 65535 f '];
    for (var k = 1; k < count; k++) rows.push(('0000000000' + (xref[k] || 0)).slice(-10) + ' 00000 n ');
    put(rows.join('\n') + '\n');
    put('trailer<</Size ' + count + '/Root 1 0 R>>\nstartxref\n' + xrefOffset + '\n%%EOF');

    var total = chunks.reduce(function (s, c) { return s + c.length; }, 0);
    var out = new Uint8Array(total);
    var p = 0;
    chunks.forEach(function (c) { out.set(c, p); p += c.length; });
    return out;
  }

  function canvasPage(canvas, wPt, hPt) {
    return {
      jpeg: dataURLToBytes(canvas.toDataURL('image/jpeg', 0.92)),
      pxW: canvas.width,
      pxH: canvas.height,
      wPt: wPt,
      hPt: hPt
    };
  }

  function savePdf(pages, filename) {
    var pdf = buildPdf(pages);
    var blob = new Blob([pdf], { type: 'application/pdf' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function buildAndDownload(blocks, dims, image, title, filename) {
    var sheet = renderSheets(blocks, dims, image, title);
    savePdf(sheet.pages.map(function (canvas) {
      return canvasPage(canvas, sheet.wPt, sheet.hPt);
    }), filename);
  }

  /* Ship already-drawn page canvases as a PDF. Exposed so other proof styles —
     the imprint proof, which is artwork on a product rather than text on a
     plate — reuse this dependency-free PDF writer instead of reimplementing it.
     Pages are assumed Letter unless a size is given. */
  function downloadCanvases(canvases, filename, opts) {
    opts = opts || {};
    var wPt = (opts.widthIn || PAGE_W_IN) * PT_PER_IN;
    var hPt = (opts.heightIn || PAGE_H_IN) * PT_PER_IN;
    savePdf(canvases.map(function (canvas) {
      return canvasPage(canvas, wPt, hPt);
    }), filename);
  }

  /* --- public entry point --- */
  // opts: { blocks: string[][], dims: {width,height,unit,image}, imageUrl,
  //         title, filename, onStatus(msg, isError) }
  function generate(opts) {
    var status = opts.onStatus || function () {};

    if (!opts.dims || !(opts.dims.width > 0) || !(opts.dims.height > 0)) {
      status('A proof size isn’t set for this option yet. Choose your options above, or contact us and we’ll send a proof.', true);
      return Promise.resolve(false);
    }

    var blocks = (opts.blocks || [])
      .map(function (b) { return (b || []).map(cleanText).map(function (s) { return s.trim(); }).filter(Boolean); })
      .filter(function (b) { return b.length; });

    if (!blocks.length) {
      status('Enter your personalization first, then download your proof.', true);
      return Promise.resolve(false);
    }

    var many = blocks.length > 1;
    status(many ? 'Building a proof of ' + blocks.length + ' items…' : 'Building your proof…');

    return loadImage(opts.imageUrl).then(function (img) {
      try {
        buildAndDownload(blocks, opts.dims, img, opts.title, opts.filename);
      } catch (e) {
        if (img) {
          // The image likely tainted the canvas (CORS) — retry without it.
          buildAndDownload(blocks, opts.dims, null, opts.title, opts.filename);
        } else {
          throw e;
        }
      }
      status(many
        ? 'Proof of ' + blocks.length + ' items downloaded — please review every item before ordering.'
        : 'Proof downloaded — please check your text and layout before ordering.');
      return true;
    }).catch(function () {
      status('Sorry — couldn’t generate the proof. Please try again.', true);
      return false;
    });
  }

  window.ArtemisProof = { generate: generate, downloadCanvases: downloadCanvases };

  /* --- wire the single-item PDP button (proof_download block) --- */
  var container = document.querySelector('[data-proof]');
  if (!container) return;
  var form = document.getElementById('product-form');
  var variantInput = document.getElementById('product-variant-id');
  if (!form || !variantInput) return;

  var btn = container.querySelector('[data-proof-download]');
  var statusEl = container.querySelector('[data-proof-status]');

  function setStatus(msg, isError) {
    if (!statusEl) return;
    statusEl.textContent = msg || '';
    statusEl.classList.toggle('product-page__proof-status--error', !!isError);
  }
  function currentDims() {
    return (window.PROOF_DIMENSIONS || {})[variantInput.value] || null;
  }
  function getLines() {
    var lines = [];
    form.querySelectorAll('input[type="text"][name^="properties["]').forEach(function (el) {
      var v = (el.value || '').trim();
      if (v) lines.push(v);
    });
    return lines;
  }

  if (btn) {
    btn.addEventListener('click', function () {
      var d = currentDims();
      generate({
        blocks: [getLines()],
        dims: d,
        imageUrl: d && d.image,
        title: (window.productData && window.productData.title) || '',
        filename: ((window.productData && window.productData.handle) || 'engraving') + '-proof.pdf',
        onStatus: setStatus
      });
    });
  }
})();
